import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import FormComponent from 'components/UI/Input/FormComponent';
import Container from 'components/UI/Layout/Container';
import Row from 'components/UI/Layout/Row';
import Col from 'components/UI/Layout/Col';
import EditableTextInput from 'components/UI/Input/EditableTextInput/index';
import produce from 'immer';
import { common as resetPasswordRules } from 'services/validation/rules/resetPassword.rules';
import Form from 'services/validation/Form';
import { faChevronRight, faKey } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MembersRepository from 'services/repositories/MembersRepository';
import _ from 'lodash';
import { useSearchParams } from 'react-router-dom';
import ErrorMessage from 'components/UI/ErrorMessage';
import LoadingSpinner from 'components/UI/LoadingIndicators/LoadingSpinner';

function ResetPasswordForm({ onSuccess })
{
    const [form, setForm] = useState(new Form({
        password: '',
        password_confirmation: '',
    }, resetPasswordRules));

    const [searchParams] = useSearchParams();
    const [fetchedResetPassword, setFetchedResetPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const token = searchParams.get('token');
    const username = searchParams.get('username');
    let resetTimeout = null;

    const handleFormChange = (_form, path) =>
    {
        const updatedForm = produce(_form, (draft) =>
        {
            draft.data[path] = _form.data[path];
        });

        setForm(updatedForm);
    };

    const fetchResetPassword = async (_form) =>
    {
        const data = {
            password: _form.data.password_confirmation,
            password_confirmation: _form.data.password_confirmation,
            token,
            username,
        };

        if (data.password !== data.password_confirmation)
        {
            setErrorMessage('Your password should match');
            return;
        }

        setIsLoading(true);
        (new MembersRepository()).savePassword(data)
            .then((response) =>
            {
                if (!response.isSuccess())
                {
                    setErrorMessage(_.get(response, 'messages.0', null) ?? 'Whoops something went wrong. If the error continues, contact technical support.');
                    return;
                }

                if (onSuccess)
                {
                    onSuccess();
                }

                setFetchedResetPassword(true);

                resetTimeout = setTimeout(() =>
                {
                    window.location.replace('/');
                }, 5000);
            }).catch((e) =>
            {
                setErrorMessage(e.message ?? 'Whoops something went wrong. If the error continues, contact technical support.');
            })
            .finally(() =>
            {
                setIsLoading(false);
            });
    };

    const handleResetPassword = () =>
    {
        const _form = produce(form, (draft) =>
        {
            draft.validate();
        });

        setForm({ ..._form });
        if (!_form.isValid())
        {
            return;
        }

        fetchResetPassword(_form);
    };

    useEffect(() => () =>
    {
        if (resetTimeout) clearTimeout(resetTimeout);
    }, []);

    return (
        <FormComponent source={form} onFormChange={handleFormChange}>
            <Container className="tw-px-4 tw-mb-4 tw-rounded-md">
                { fetchedResetPassword
                    && (
                        <Col className="tw-mb-3 tw-mt-3 tw-justify-center">
                            <p className="tw-text-center">
                                Your password has been updated, you will be redirect to the login page in 5 seconds, you can also go immediately by <a className="tw-text-primary-link hover:tw-text-primary-link-hover" href="/">clicking here.</a>.
                            </p>
                        </Col>
                    )}

                { !fetchedResetPassword && (
                    <>
                        <ErrorMessage errorMessage={errorMessage} />

                        <Row className="tw-mb-3 tw-justify-center">
                            <span className="tw-font-regular">Please enter your new password <span className="tw-font-semibold">{username}</span>.</span>
                        </Row>

                        <Row className="tw-mb-3 tw-w-full">
                            <Col className="tw-w-full">
                                <EditableTextInput
                                    editing={!isLoading}
                                    containerClassName="tw-mb-3"
                                    labelText="New Password: "
                                    text={form.data.password}
                                    inputProps={{
                                        type: 'password',
                                        path: 'password',
                                        error: form.errorsBag.errors.password,
                                        icon: faKey,
                                    }}
                                />

                                <EditableTextInput
                                    editing={!isLoading}
                                    labelText="Confirm Password: "
                                    containerClassName="tw-mb-3"
                                    text={form.data.password_confirmation}
                                    inputProps={{
                                        type: 'password',
                                        path: 'password_confirmation',
                                        error: form.errorsBag.errors.password_confirmation,
                                        icon: faKey,
                                    }}
                                />
                            </Col>
                        </Row>

                        <Row className="tw-text-right tw-mt-5" reversed>
                            <Button
                                className="tw-py-2"
                                onClick={() => handleResetPassword()}
                                disabled={isLoading}
                            >
                                Next {!isLoading && <FontAwesomeIcon className="tw-ml-4" icon={faChevronRight} />} <LoadingSpinner className="tw-ml-4" loading={isLoading} />
                            </Button>
                        </Row>
                    </>
                )}
            </Container>
        </FormComponent>
    );
}

ResetPasswordForm.propTypes = {
    onSuccess: PropTypes.func,
};

export default ResetPasswordForm;
